package com.imobly.imobly.viewmodel

import androidx.compose.material3.SnackbarHostState
import androidx.compose.runtime.MutableState
import androidx.compose.runtime.mutableStateOf
import androidx.lifecycle.ViewModel
import androidx.lifecycle.viewModelScope
import androidx.navigation.NavHostController
import com.imobly.imobly.api.createHttpClient
import com.imobly.imobly.api.createViaCepHttpClient
import com.imobly.imobly.api.dto.ErrorDTO
import com.imobly.imobly.api.dto.Ok
import com.imobly.imobly.api.dto.viacep.ErrorViaCepDTO
import com.imobly.imobly.api.dto.viacep.ViaCepDTO
import com.imobly.imobly.api.httpclient.AuthenticationTenantHttpClient
import com.imobly.imobly.api.httpclient.TenantHttpClient
import com.imobly.imobly.api.httpclient.ViaCepHttpClient
import com.imobly.imobly.domain.Telephone
import com.imobly.imobly.domain.Tenant
import com.imobly.imobly.domain.enums.MaritalStatusEnum
import io.github.ismoy.imagepickerkmp.domain.models.GalleryPhotoResult
import kotlinx.coroutines.launch

class TenantViewModel(private val navController: NavHostController) : ViewModel() {

    val tenants: MutableState<List<Tenant>> = mutableStateOf(emptyList())
    val currentPage = mutableStateOf(0)
    val totalPages = mutableStateOf(0)

    val tenant = mutableStateOf(Tenant())

    val searchText: MutableState<String> = mutableStateOf("")

    val selectedImages = mutableStateOf(emptyList<GalleryPhotoResult>())

    val inputLockState = mutableStateOf(true)

    val onLoadingState = mutableStateOf(false)


    val inputErrors = mutableStateOf(emptyMap<String, String>())

    val messageError = mutableStateOf("")

    val showDialogState = mutableStateOf(false)

    val snackMessage: MutableState<SnackbarHostState> = mutableStateOf(SnackbarHostState())

    val passwordVisibilityState = mutableStateOf(false)

    val active = mutableStateOf(true)

    fun changePasswordVisibility() {
        passwordVisibilityState.value = !passwordVisibilityState.value
    }

    fun changeShowDialog() {
        showDialogState.value = !showDialogState.value
    }

    fun whenStartingThePage() {
        snackMessage.value = SnackbarHostState()
        onLoadingState.value = false
    }

    fun resetPage() {
        tenant.value = Tenant()
        selectedImages.value = emptyList()
        inputErrors.value = emptyMap()
        messageError.value = ""
        searchText.value = ""
    }

    fun hiddenEditButton() {
        inputLockState.value = !inputLockState.value
    }

    fun changeSearchText(it: String) {
        searchText.value = it
    }

    fun inputContainsError(inputLabel: String): Boolean {
        return inputErrors.value.keys.contains(inputLabel)
    }

    fun getInputErrorMessage(inputLabel: String): String {
        return inputErrors.value[inputLabel] ?: ""
    }

    fun loadPage(page: Int) {
        viewModelScope.launch {
            onLoadingState.value = true
            val httpClient = TenantHttpClient(createHttpClient())
            try {
                val paginated = httpClient.searchAllByNameOrCpfAndActive(searchText.value, active.value, page)
                tenants.value = paginated.tenants
                totalPages.value = paginated.totalPages
                currentPage.value = page
            } catch (e: Exception) {
                messageError.value = "Erro ao carregar locatários: ${e.message}"
            } finally {
                onLoadingState.value = false
            }
        }
    }

    fun findAllAction() {
        loadPage(0)
    }

    fun goToCreateTenant() {
        navController.navigate("createtenant")
    }

    fun goToEditTenant(newTenant: Tenant) {
        tenant.value = newTenant
        inputLockState.value = true
        inputErrors.value = emptyMap()
        messageError.value = ""
        selectedImages.value = emptyList()
        navController.navigate("edittenant")
    }

    fun goToHome() {
        navController.navigate("home")
    }

    fun goToShowTenants() {
        navController.navigate("showtenants")
    }

    fun searchAction() {
        loadPage(0)
    }

    fun createAction() {
        viewModelScope.launch {
            onLoadingState.value = true
            val httpClient = AuthenticationTenantHttpClient(createHttpClient())
            var imageToSend: GalleryPhotoResult? = null
            if (selectedImages.value.isNotEmpty()) {
                imageToSend = selectedImages.value.first()
            }
            val response = httpClient.createTenant(tenant.value, imageToSend)
            onLoadingState.value = false
            when (response) {
                is Ok -> {
                    tenant.value = Tenant()
                    selectedImages.value = emptyList()
                    inputErrors.value = emptyMap()
                    messageError.value = ""
                    snackMessage.value.showSnackbar("Locatário criado com sucesso!")
                }

                is ErrorDTO -> {
                    val errors = mutableMapOf<String, String>()
                    response.errorFields?.forEach { errors[it.name] = it.description }
                    inputErrors.value = errors
                    messageError.value = response.message
                }
            }
        }
    }

    fun editAction() {
        viewModelScope.launch {
            onLoadingState.value = true
            val httpClient = TenantHttpClient(createHttpClient())
            if (tenant.value.id != null) {
                var imageToSend: GalleryPhotoResult? = null
                if (selectedImages.value.isNotEmpty()) {
                    imageToSend = selectedImages.value.first()
                }
                val response = httpClient.update(tenant.value.id!!, tenant.value, imageToSend)
                onLoadingState.value = false
                when (response) {
                    is Ok -> {
                        hiddenEditButton()
                        snackMessage.value.showSnackbar("Locatário editado com sucesso!")
                    }

                    is ErrorDTO -> {
                        val errors = mutableMapOf<String, String>()
                        response.errorFields?.forEach { errors[it.name] = it.description }
                        inputErrors.value = errors
                        messageError.value = response.message
                    }
                }
            }
        }
    }

    fun toggleEnableAction() {
        viewModelScope.launch {
            val httpClient = TenantHttpClient(createHttpClient())
            if (tenant.value.id != null) {
                val response = httpClient.toggleEnable(tenant.value.id!!)
                showDialogState.value = false
                when (response) {
                    is Ok -> {
                        if (tenant.value.isEnabled) {
                            snackMessage.value.showSnackbar("Locatário desativado com sucesso!")
                        } else {
                            snackMessage.value.showSnackbar("Locatário ativado com sucesso!")
                        }
                        tenant.value = httpClient.searchAllByNameOrCpfAndActive(tenant.value.cpf).tenants.first()
                    }

                    is ErrorDTO -> {
                        if (tenant.value.isEnabled) {
                            messageError.value = response.message
                            snackMessage.value.showSnackbar("Houve um problema ao desativar o locatário!")
                        } else {
                            messageError.value = response.message
                            snackMessage.value.showSnackbar("Houve um problema ao ativar o locatário!")
                        }
                    }
                }
            }
        }
    }

    fun deleteAction() {
        viewModelScope.launch {
            val httpClient = TenantHttpClient(createHttpClient())
            if (tenant.value.id != null) {
                val response = httpClient.delete(tenant.value.id!!)

                showDialogState.value = false
                when (response) {
                    is Ok -> {
                        goToShowTenants()
                        snackMessage.value.showSnackbar("Locatário deletado com sucesso!")
                    }

                    is ErrorDTO -> {
                        messageError.value = response.message
                    }
                }
            }
        }
    }

    fun getAddressViaCepAction() {
        if (tenant.value.address.cep.length in 8 .. 9) {
            viewModelScope.launch {
                val httpClient = ViaCepHttpClient(createViaCepHttpClient())
                when (val response = httpClient.getAddress(tenant.value.address.cep)) {
                    is ViaCepDTO -> {
                        changeState(response.estado)
                        changeCity(response.localidade)
                        changeNeighborhood(response.bairro)
                        changeStreet(response.logradouro)
                    }

                    is ErrorViaCepDTO -> {
                        snackMessage.value.showSnackbar(response.message)
                    }
                }
            }
        }
    }

    fun activeOptions(): Map<String, String> {
        return mapOf(Pair("ATIVOS", "true"), Pair("DESATIVADOS", "false"))
    }

    fun onOptionSelectedActive(activeSelected: String) {
        active.value = activeSelected == "true"
        searchAction()
    }

    fun selectedOption() =
        when (active.value) {
            true -> "ATIVOS"
            false -> "DESATIVADOS"
        }


    fun maritalStatusOptions(): Map<String, String> {
        val map = mutableMapOf<String, String>()
        MaritalStatusEnum.entries.forEach {
            map[it.label] = it.name
        }
        return map
    }

    fun changeFirstName(it: String) {
        tenant.value = tenant.value.copy(firstName = it)
    }

    fun changeLastName(it: String) {
        tenant.value = tenant.value.copy(lastName = it)
    }

    fun changeEmail(it: String) {
        tenant.value = tenant.value.copy(email = it)
    }

    fun changePassword(it: String) {
        tenant.value = tenant.value.copy(password = it)
    }

    fun changeTelephoneOne(it: String) {
        tenant.value = tenant.value.copy(
            telephones = Telephone(
                it,
                tenant.value.telephones.telephone2,
                tenant.value.telephones.telephone3
            )
        )
    }

    fun changeTelephoneTwo(it: String) {
        tenant.value = tenant.value.copy(
            telephones = Telephone(
                tenant.value.telephones.telephone1,
                it,
                tenant.value.telephones.telephone3
            )
        )
    }

    fun changeTelephoneThree(it: String) {
        tenant.value = tenant.value.copy(
            telephones = Telephone(
                tenant.value.telephones.telephone1,
                tenant.value.telephones.telephone2,
                it
            )
        )
    }

    fun changeNationality(it: String) {
        tenant.value = tenant.value.copy(nationality = it)
    }

    fun changeJob(it: String) {
        tenant.value = tenant.value.copy(job = it)
    }

    fun changeCpf(it: String) {
        tenant.value = tenant.value.copy(cpf = it)
    }

    fun changeRg(it: String) {
        tenant.value = tenant.value.copy(rg = it)
    }

    fun changeBirthDate(it: String) {
        tenant.value = tenant.value.copy(birthDate = it)
    }

    fun changeCep(it: String) {
        tenant.value = tenant.value.copy(
            address = tenant.value.address.copy(cep = it)
        )
        getAddressViaCepAction()
    }

    fun changeState(it: String) {
        tenant.value = tenant.value.copy(
            address = tenant.value.address.copy(state = it)
        )
    }

    fun changeCity(it: String) {
        tenant.value = tenant.value.copy(
            address = tenant.value.address.copy(city = it)
        )
    }

    fun changeNeighborhood(it: String) {
        tenant.value = tenant.value.copy(
            address = tenant.value.address.copy(neighborhood = it)
        )
    }

    fun changeStreet(it: String) {
        tenant.value = tenant.value.copy(
            address = tenant.value.address.copy(street = it)
        )
    }

    fun changeNumber(it: String) {
        tenant.value = tenant.value.copy(
            address = tenant.value.address.copy(
                number = if (it.toIntOrNull() != null || it == "") {
                    it
                } else {
                    tenant.value.address.number
                }
            )
        )
    }

    fun changeComplement(it: String) {
        tenant.value = tenant.value.copy(
            address = tenant.value.address.copy(complement = it)
        )
    }
}